import React from 'react';
import { IoClose } from 'react-icons/io5';

interface FilterUIProps {
  showFilters: boolean;
  selectedType: string[];
  selectedCredits: string[];
  selectedVenue: string[];
  activeCategory: 'type' | 'credits' | 'venue';
  filterOptions: {
    types: string[];
    credits: string[];
    venues: string[];
  };
  onTypeSelect: (type: string) => void;
  onCreditsSelect: (credits: string) => void;
  onVenueSelect: (venue: string) => void;
  onClearType: () => void;
  onClearCredits: () => void;
  onClearVenue: () => void;
  onCategorySelect: (category: 'type' | 'credits' | 'venue') => void;
  onClose: () => void;
}

const FilterUI: React.FC<FilterUIProps> = ({
  showFilters,
  selectedType,
  selectedCredits,
  selectedVenue,
  activeCategory,
  filterOptions,
  onTypeSelect,
  onCreditsSelect,
  onVenueSelect,
  onClearType,
  onClearCredits,
  onClearVenue,
  onCategorySelect,
  onClose
}) => {
  if (!showFilters) return null;

  return (
    <div className="absolute z-10 mt-3 w-full bg-white rounded-xl border border-gray-200 shadow-lg backdrop-blur-sm bg-white/95">
      <div className="flex">
        {/* Categories Side Menu */}
        <div className="w-48 border-r border-gray-200 p-4 space-y-4">
          <div 
            onClick={() => onCategorySelect('type')}
            className={`cursor-pointer py-2 px-3 rounded-lg transition-colors ${activeCategory === 'type' ? 'bg-black/5 text-black' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            Course Type {selectedType.length > 0 && '•'}
          </div>
          <div 
            onClick={() => onCategorySelect('credits')}
            className={`cursor-pointer py-2 px-3 rounded-lg transition-colors ${activeCategory === 'credits' ? 'bg-black/5 text-black' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            Credits {selectedCredits.length > 0 && '•'}
          </div>
          <div 
            onClick={() => onCategorySelect('venue')}
            className={`cursor-pointer py-2 px-3 rounded-lg transition-colors ${activeCategory === 'venue' ? 'bg-black/5 text-black' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            Venue {selectedVenue.length > 0 && '•'}
          </div>
        </div>

        {/* Filter Options */}
        <div className="flex-1 p-5 max-h-[70vh] overflow-y-auto">
          {activeCategory === 'type' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Course Type</h3>
                {selectedType.length > 0 && (
                  <button
                    onClick={onClearType}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear
                  </button>
                )}
              </div>
              {filterOptions.types.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {filterOptions.types.map(type => (
                    <div
                      key={type}
                      onClick={() => onTypeSelect(type)}
                      className={`
                        cursor-pointer p-3 rounded-xl border transition-all
                        ${selectedType.includes(type)
                          ? 'border-black bg-black/5 text-black' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center
                          ${selectedType.includes(type)
                            ? 'border-black bg-black' 
                            : 'border-gray-300'}`}
                        >
                          {selectedType.includes(type) && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No course types available
                </div>
              )}
            </div>
          )}

          {activeCategory === 'credits' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Credits</h3>
                {selectedCredits.length > 0 && (
                  <button
                    onClick={onClearCredits}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear
                  </button>
                )}
              </div>
              {filterOptions.credits.length > 0 ? (
                <div className="grid grid-cols-4 gap-3">
                  {filterOptions.credits.map(credit => (
                    <div
                      key={credit}
                      onClick={() => onCreditsSelect(credit)}
                      className={`
                        cursor-pointer p-3 rounded-xl border transition-all text-center
                        ${selectedCredits.includes(credit)
                          ? 'border-black bg-black/5 text-black' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'}
                      `}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center
                          ${selectedCredits.includes(credit)
                            ? 'border-black bg-black' 
                            : 'border-gray-300'}`}
                        >
                          {selectedCredits.includes(credit) && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{credit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No credit options available
                </div>
              )}
            </div>
          )}

          {activeCategory === 'venue' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Venue</h3>
                {selectedVenue.length > 0 && (
                  <button
                    onClick={onClearVenue}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear
                  </button>
                )}
              </div>
              {filterOptions.venues.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {filterOptions.venues.map(venue => (
                    <div
                      key={venue}
                      onClick={() => onVenueSelect(venue)}
                      className={`
                        cursor-pointer p-3 rounded-xl border transition-all
                        ${selectedVenue.includes(venue)
                          ? 'border-black bg-black/5 text-black' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center
                          ${selectedVenue.includes(venue)
                            ? 'border-black bg-black' 
                            : 'border-gray-300'}`}
                        >
                          {selectedVenue.includes(venue) && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{venue}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No venues available
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filter Actions */}
      <div className="border-t border-gray-200 p-4 flex justify-between items-center bg-gray-50 rounded-b-xl">
        <button
          onClick={() => {
            onClearType();
            onClearCredits();
            onClearVenue();
          }}
          className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1.5"
        >
          <IoClose className="h-4 w-4" />
          Clear all
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-black hover:bg-gray-900 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Apply filters
        </button>
      </div>
    </div>
  );
};

export default FilterUI; 